<script>
import { mapActions } from 'vuex'

export default {
  name: 'ProgramsList',
  components: {
    Action: () => import('@/components/general/Action'),
    Dropdown: () => import('@/components/general/Dropdown'),
    Datatable: () => import('@/components/general/Datatable'),
    FilterList: () => import('@/components/general/FilterList'),
    Pagination: () => import('@/components/general/Pagination'),
    DropdownLink: () => import('@/components/general/DropdownLink'),
    ContentHeader: () => import('@/components/general/ContentHeader'),
    FilterListSearch: () => import('@/components/general/FilterListSearch'),
    Tabs: () => import('@/components/general/Tabs'),
    SelectField: () => import('@/components/general/SelectField'),
    PageLimitSelector: () => import('@/components/general/PageLimitSelector')
  },

  data () {
    return {
      pagination: {
        page: 1,
        limit: 10,
        lastPage: null,
        filter: {
          only_name: true
        }
      },

      search: null,
      modalToggleActiveOpen: false,
      programId: null,
      genreFilter: null,
      genreFilterList: [
        {
          text: this.$t('programs.list:filter.1'),
          value: 'curso_ead'
        },
        {
          text: this.$t('programs.list:filter.2'),
          value: 'dicas_whatsapp'
        },
        {
          text: this.$t('programs.list:filter.3'),
          value: 'presencial'
        }
      ],

      tabLinks: [
        {
          location: { name: 'programs.index' },
          text: this.$t('global:menu.programs')
        },
        {
          location: { name: 'programs.configs' },
          text: this.$t('solutions.tabs:link.2')
        }
      ]
    }
  },

  computed: {
    programsList () {
      return this.$store.getters.programsList
    },

    canWrite () {
      return this.getContextPermission('programs') === 'write'
    },

    isNonProdEnv () {
      return process.env.NODE_ENV !== 'production'
    }
  },

  watch: {
    'pagination.page' () {
      this.getProgramList()
    }
  },

  created () {
    this.getProgramList()
  },

  methods: {
    ...mapActions([
      'setFetching',
      'setFeedback',
      'attemptGetProgramsList',
      'attemptManageProgram',
      'attemptToggleActiveProgram',
      'attemptUpdateProgram'
    ]),

    getToggleClass (isEnabled) {
      return isEnabled ? 'dropdown-toggle--on' : 'dropdown-toggle--off'
    },

    prevPage () {
      if (this.pagination.page > 1) {
        this.pagination.page -= 1
      }
    },

    nextPage () {
      this.pagination.page += 1
    },

    firstPage () {
      this.pagination.page = 1
    },

    lastPage () {
      this.pagination.page = this.pagination.lastPage
    },

    getProgramList () {
      this.setFetching(true)

      this.pagination.order = {
        active: 'desc',
        name: 'asc'
      }

      if (this.search !== '' && this.search !== null) {
        this.attemptGetProgramsList(this.pagination)
          .then(pagination => {
            this.pagination.lastPage = pagination.lastPage
          })
          .catch((error) => {
            // eslint-disable-next-line no-console
            console.error('[ProgramsList] Falha ao carregar a listagem de programas', error)
            this.setFeedback({ message: this.$t('programs.load:fetch.error') })
          })
          .finally(() => {
            this.setFetching(false)
          })

        return
      }

      this.attemptGetProgramsList(this.pagination)
        .then(pagination => {
          this.pagination.lastPage = pagination.lastPage
        })
        .catch((error) => {
          // eslint-disable-next-line no-console
          console.error('[ProgramsList] Falha ao carregar a listagem de programas', error)
          this.setFeedback({ message: this.$t('programs.load:fetch.error') })
        })
        .finally(() => {
          this.setFetching(false)
        })
    },

    onLimitChange (newLimit) {
      this.pagination.limit = newLimit

      if (this.pagination.page === 1) {
        this.getProgramList()
      } else {
        this.pagination.page = 1
      }
    },

    getProgramLength (programId) {
      const program = this.programsList.find(program => program.id === programId)

      return program.programSolutions.length
    },

    createNewProgram () {
      this.attemptManageProgram(null)
      this.$router.push({ name: 'programs.manage' })
    },

    searchProgram (search) {
      this.search = search

      this.pagination.query = {
        name: search
      }

      this.firstPage()
      this.getProgramList()
    },

    edit (programId, tab = 1) {
      this.$router.push({
        name: 'programs.manage',
        params: {
          id: programId,
          tab: tab
        }
      })
    },

    openPage (program) {
      if (program.partner && program.partner.id) {
        window.open(`${process.env.VUE_APP_PORTAL_URL}conecta-saber/programa/${program.slug}`, '_blank')

        return
      }

      window.open(`${process.env.VUE_APP_PORTAL_URL}programa/${program.slug}`, '_blank')
    },

    toggleActive (programId, toggleFlag) {
      this.setFetching(true)

      this.attemptToggleActiveProgram(programId)
        .then(() => {
          this.getProgramList()
          const toggleValue = toggleFlag ? 'deactivate' : 'activate'

          this.setFeedback({ message: this.$t(`programs:feedback.toggle.${toggleValue}.success`) })
        })
        .catch((e) => {
          if (e.data.error.message === 'program_solution_not_found') {
            this.setFeedback({ message: this.$t('global:form.programs.toggle.program.solution.not.error') })
          } else if (e.data.error.message === 'program_workload_empty') {
            this.setFeedback({ message: this.$t('global:form.programs.toggle.program.workload.error') })
          } else {
            this.setFeedback({ message: this.$t('global.error') })
          }
        })
        .finally(() => {
          this.setFetching(false)
        })
    },

    buildProgramUpdateData (apiData) {
      const themes = []

      if (apiData.programThemes) {
        apiData.programThemes.forEach(pt => themes.push(pt.theme.alias))
      }

      const entrepreneurProfiles = []

      if (apiData.programEntrepreneurProfiles) {
        apiData.programEntrepreneurProfiles.forEach(pep => entrepreneurProfiles.push(pep.entrepreneurProfile.id))
      }

      const solutions = []

      if (apiData.programSolutions) {
        apiData.programSolutions.filter(s => s.active === 1).forEach(ps => {
          solutions.push({
            id: ps.id,
            required: ps.required,
            dependent: ps.dependent || null
          })
        })
      }

      const thematicPages = []

      if (apiData.thematicPagesProgram) {
        apiData.thematicPagesProgram.forEach(tp => thematicPages.push(tp.id))
      }

      const surveyIds = []

      if (apiData.programSurvey) {
        apiData.programSurvey.forEach(ps => surveyIds.push(ps.survey.id))
      }

      return {
        id: apiData.id,
        name: apiData.name,
        slug: apiData.slug,
        description: apiData.description,
        active: apiData.active,
        workload: apiData.workload || '0h',
        automaticWorkload: apiData.automaticWorkload ? 1 : 0,
        conclusionDeadline: apiData.conclusionDeadline,
        call_text: apiData.callText,
        preview_video: apiData.previewVideo,
        audience: apiData.audience,
        differentials: apiData.differentials,
        show_on_portal: apiData.showOnPortal ? 1 : 0,
        exclusiveToCompany: apiData.exclusiveToCompany ? 1 : 0,
        exclusiveToThematicPages: apiData.exclusiveToThematicPages ? 1 : 0,
        portfolio_id: apiData.portfolioId || null,
        sas_id: apiData.sasId || null,
        seoTitle: apiData.seoTitle || apiData.name,
        seoDescription: apiData.seoDescription || apiData.description,
        seoKeywords: apiData.seoKeywords || '',
        certificate_id: apiData.certificate?.id || null,
        themes: themes,
        entrepreneur_profiles: entrepreneurProfiles,
        payment_type: apiData.paymentType || 'free',
        price: apiData.price,
        solutions: solutions,
        thematicPages: thematicPages,
        tags: apiData.tags || [],
        survey_ids: surveyIds,
        survey_required: apiData.surveyRequired || 0,
        survey_required_for_certificate: apiData.surveyRequiredForCertificate ? 1 : 0,
        registrationStatus: apiData.registrationStatus
      }
    },

    toggleField (programId, fieldName, currentValue) {
      this.setFetching(true)

      this.attemptManageProgram(programId)
        .then(({ data }) => {
          const updateData = this.buildProgramUpdateData(data)
          const newValue = currentValue ? 0 : 1

          updateData[fieldName] = newValue

          return this.attemptUpdateProgram({
            id: programId,
            data: updateData,
            isLinkedTab: false
          })
        })
        .then(() => {
          this.getProgramList()
          const action = currentValue ? 'desativado' : 'ativado'
          const fieldLabels = {
            show_on_portal: 'Exibir no portal',
            exclusiveToCompany: 'Exclusivo PJ'
          }

          this.setFeedback({ message: `${fieldLabels[fieldName] || fieldName} ${action} com sucesso` })
        })
        .catch(() => {
          this.setFeedback({ message: this.$t('global.error') })
        })
        .finally(() => {
          this.setFetching(false)
        })
    }
  }
}
</script>

<template>
  <div class="main-content">
    <ContentHeader
      :title="$t('programs:header.title')"
      :description="$t('programs:header.description')"
      background="/assets/images/themes/default/png/tracks_banner.png"
    >
      <Tabs
        v-if="canWrite"
        slot="tabs"
        dark
        :links="tabLinks"
      />
    </ContentHeader>
    <FilterList>
      <Action
        v-if="canWrite"
        slot="button"
        primary
        large
        fixed-width
        type="button"
        :text="$t('programs.list:btn.add')"
        @click="createNewProgram"
      />
      <SelectField
        v-model="genreFilter"
        :label="$t('programs.list:filter.1')"
        :items="genreFilterList"
      />
      <PageLimitSelector
        slot="order"
        :value="pagination.limit"
        storage-key="page_limit_programs"
        @input="onLimitChange"
      />
      <FilterListSearch
        slot="search"
        :hide-error-details="true"
        @search="searchProgram"
      />
    </FilterList>
    <div
      v-if="programsList.length > 0"
      class="center programs-table"
    >
      <Datatable :items="programsList">
        <template slot="thead">
          <tr>
            <th class="th">
              {{ $t('programs.list:datatable.header.1') }}
            </th>
            <th class="th">
              {{ $t('programs.list:datatable.header.2') }}
            </th>

            <th class="th text-center">
              {{ $t('solutions.list:datatable.header.6') }}
            </th>

            <th class="th">
              {{ $t('programs.list:datatable.header.3') }}
            </th>
            <th
              class="th"
              width="130"
            >
              {{ $t('global:datatable.audit.created-at') }}
            </th>
            <th
              class="th"
              width="180"
            >
              {{ $t('global:datatable.audit.updated-at') }}
            </th>
            <th
              class="th"
              width="40"
            />
          </tr>
        </template>
        <template
          slot="tbody"
          slot-scope="props"
        >
          <tr>
            <td class="td">
              <span class="td-text bolder">{{ props.item.name }}</span>
            </td>
            <td class="td">
              <span class="td-text">{{ getProgramLength(props.item.id) }}</span>
            </td>

            <td class="td text-center">
              <span
                :class="{ 'td-tag active-ribbon': Boolean(props.item.showOnPortal), 'td-tag inactive-ribbon': Boolean(!props.item.showOnPortal) }"
              >
                {{ Boolean(props.item.showOnPortal) ? $t('global:yes') : $t('global:no') }}</span>
            </td>

            <td class="td">
              <span
                v-if="props.item.active"
                class="td-tag active-ribbon"
              >{{ $t('global.status:active') }}</span>
              <span
                v-else
                class="td-tag inactive-ribbon"
              >{{ $t('global.status:inactive') }}</span>
            </td>
            <td class="td">
              <span class="td-text">{{ formatDate(props.item.createdAt) || '—' }}</span>
            </td>
            <td class="td">
              <span class="td-text td-block">{{ formatDate(props.item.userUpdatedAt || props.item.createdAt) || '—' }}</span>
              <span
                v-if="props.item.userUpdatedAt || props.item.createdAt"
                class="td-text td-audit-sub"
              >{{ formatTime(props.item.userUpdatedAt || props.item.createdAt) }}h{{ auditAuthorLabel(props.item) }}</span>
            </td>
            <td
              class="td td-actions"
              width="40"
            >
              <Dropdown
                icon="dots-vertical"
                right
              >
                <DropdownLink
                  v-if="canWrite"
                  :text="$t('global:edit')"
                  @click="edit(props.item.id)"
                />
                <DropdownLink
                  :text="$t('global:qualify')"
                  @click="edit(props.item.id, 2)"
                />
                <DropdownLink
                  :text="$t('global:integrations')"
                  @click="edit(props.item.id, 3)"
                />
                <DropdownLink
                  :text="$t('global:seo')"
                  @click="edit(props.item.id, 4)"
                />
                <DropdownLink
                  :text="$t('global:links')"
                  @click="edit(props.item.id, 5)"
                />
                <DropdownLink
                  :text="$t('global:show.page')"
                  @click="openPage(props.item)"
                />
                <DropdownLink
                  v-if="!props.item.active && canWrite"
                  :text="$t('global:activate')"
                  @click="toggleActive(props.item.id, props.item.active)"
                />
                <DropdownLink
                  v-else-if="canWrite"
                  :text="$t('global:deactivate')"
                  @click="toggleActive(props.item.id, props.item.active)"
                />
                <template v-if="isNonProdEnv && canWrite">
                  <div class="dropdown-devqa-separator">
                    <span class="dropdown-devqa-title">Ações rápidas (DEV/QA)</span>
                  </div>
                  <div
                    :class="['dropdown-toggle-wrapper', getToggleClass(Boolean(props.item.active))]"
                  >
                    <DropdownLink
                      :text="`Situação: ${Boolean(props.item.active) ? 'Ativo' : 'Inativo'}`"
                      @click="toggleActive(props.item.id, props.item.active)"
                    />
                  </div>
                  <div
                    v-if="typeof props.item.showOnPortal !== 'undefined'"
                    :class="['dropdown-toggle-wrapper', getToggleClass(Boolean(props.item.showOnPortal))]"
                  >
                    <DropdownLink
                      :text="`Exibir no portal: ${Boolean(props.item.showOnPortal) ? 'Sim' : 'Não'}`"
                      @click="toggleField(props.item.id, 'show_on_portal', props.item.showOnPortal)"
                    />
                  </div>
                  <div
                    v-if="typeof props.item.exclusiveToCompany !== 'undefined'"
                    :class="['dropdown-toggle-wrapper', getToggleClass(Boolean(props.item.exclusiveToCompany))]"
                  >
                    <DropdownLink
                      :text="`Exclusivo PJ: ${Boolean(props.item.exclusiveToCompany) ? 'Sim' : 'Não'}`"
                      @click="toggleField(props.item.id, 'exclusiveToCompany', props.item.exclusiveToCompany)"
                    />
                  </div>
                </template>
              </Dropdown>
            </td>
          </tr>
        </template>
      </Datatable>
      <Pagination
        :active-page="pagination.page"
        :page-count="pagination.lastPage"
        @firstPage="firstPage"
        @lastPage="lastPage"
        @nextPage="nextPage"
        @previousPage="prevPage"
      />
    </div>
    <div
      v-else
      class="center programs-table"
    >
      <p>Nenhum programa encontrado para o termo pesquisado</p>
    </div>
  </div>
</template>

<style src="@/assets/styles/themes/default/programs.css"></style>
